import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { signInWithGoogle, signInWithApple } from '../lib/firebase'
import { Loader2, Brain, Library, Search, Zap, ArrowRight, Monitor, Smartphone, Tablet, Cloud, RefreshCw } from 'lucide-react'

export default function LoginPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(null) // null | 'google' | 'apple'
  const [error, setError] = useState(null)

  // Force dark mode on login page
  useEffect(() => {
    document.documentElement.classList.add('dark')
    return () => {
      // Don't remove dark mode on unmount - let the app's theme setting take over
    }
  }, [])

  const handleGoogleSignIn = async () => {
    setLoading('google')
    setError(null)
    try {
      await signInWithGoogle()
      navigate('/')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(null)
    }
  }

  const handleAppleSignIn = async () => {
    setLoading('apple')
    setError(null)
    try {
      await signInWithApple()
      navigate('/')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Enhanced Aurora Background for dark theme */}
      <div className="aurora-bg">
        <div className="absolute w-[800px] h-[800px] rounded-full bg-indigo-600/20 blur-[120px] -top-[300px] -right-[200px] animate-aurora-float" />
        <div className="absolute w-[600px] h-[600px] rounded-full bg-purple-600/15 blur-[100px] -bottom-[200px] -left-[150px] animate-aurora-float" style={{ animationDelay: '-7s' }} />
        <div className="absolute w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-[80px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-aurora-float" style={{ animationDelay: '-14s' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo and title */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-2xl shadow-indigo-500/30 flex items-center justify-center">
                <img
                  src="/assets/logo.png"
                  alt="SigmaNote"
                  className="w-14 h-14 rounded-xl"
                />
              </div>
              {/* Glow rings */}
              <div className="absolute inset-0 rounded-3xl bg-indigo-500/20 animate-pulse-ring" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-3 text-white">
            Welcome to <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">SigmaNote</span>
          </h1>
          <p className="text-lg text-gray-400">
            Your AI-powered personal knowledge hub
          </p>
        </div>

        {/* Cross-platform badge */}
        <div className="flex justify-center mb-6 animate-fade-in" style={{ animationDelay: '0.05s' }}>
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
            <div className="flex items-center gap-1.5">
              <Monitor className="w-4 h-4 text-indigo-400" />
              <Tablet className="w-4 h-4 text-purple-400" />
              <Smartphone className="w-4 h-4 text-pink-400" />
            </div>
            <span className="text-sm text-gray-300 font-medium">Available on all your devices</span>
          </div>
        </div>

        {/* Login card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl p-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2 text-white">Sign in to continue</h2>
              <p className="text-sm text-gray-400">
                Access your notes, documents, and AI assistant
              </p>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 text-red-400 text-sm border border-red-500/20">
                {error}
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              disabled={loading !== null}
              className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-white/5 border border-white/10 hover:border-indigo-500/50 hover:bg-white/10 hover:shadow-lg hover:shadow-indigo-500/10 transition-all disabled:opacity-50 group"
            >
              {loading === 'google' ? (
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className="font-medium text-white">Continue with Google</span>
              <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all ml-auto" />
            </button>

            <button
              onClick={handleAppleSignIn}
              disabled={loading !== null}
              className="w-full flex items-center justify-center gap-3 px-5 py-4 rounded-2xl bg-white text-gray-900 border border-white hover:bg-gray-100 transition-all disabled:opacity-50 group"
            >
              {loading === 'apple' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              )}
              <span className="font-medium">Continue with Apple</span>
              <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all ml-auto" />
            </button>

            <p className="text-xs text-center text-gray-500">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>

        {/* Features preview */}
        <div className="mt-8 grid grid-cols-3 gap-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {[
            { icon: Library, label: 'Knowledge Base', color: '#4ECDC4' },
            { icon: Brain, label: 'AI Assistant', color: '#818cf8' },
            { icon: Search, label: 'Smart Search', color: '#f472b6' },
          ].map((feature) => (
            <div
              key={feature.label}
              className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all group cursor-default"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 mx-auto transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
              </div>
              <div className="text-xs text-gray-400 text-center font-medium">
                {feature.label}
              </div>
            </div>
          ))}
        </div>

        {/* Cross-platform sync highlight */}
        <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-white/10 animate-fade-in" style={{ animationDelay: '0.25s' }}>
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-indigo-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white mb-0.5">Seamless Cross-Platform Sync</h3>
              <p className="text-xs text-gray-400">Your knowledge, perfectly synchronized across web, iOS, and Android</p>
            </div>
            <Cloud className="w-5 h-5 text-purple-400 flex-shrink-0" />
          </div>
        </div>

        {/* Mobile app callout */}
        <div className="mt-4 flex items-center justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <a
            href="https://apps.apple.com/us/app/sigmanote-your-second-brain/id6744374542"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
          >
            <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Download on the App Store</span>
          </a>
        </div>

        {/* Tagline */}
        <div className="mt-6 text-center animate-fade-in" style={{ animationDelay: '0.35s' }}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400">
            <Zap className="w-4 h-4 text-indigo-400" />
            <span>Import anything. Ask anything. Know everything.</span>
          </div>
        </div>
      </div>
    </div>
  )
}
